import React from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, X, Settings2, Maximize2, Shield, Users } from 'lucide-react';
// 🛰️ Using unified types to ensure WidgetId consistency
import { WidgetId } from '@/types';

interface WidgetContainerProps {
  id: WidgetId;
  title: string;
  children: React.ReactNode;
  isEditMode: boolean;
  onRemove: (id: WidgetId) => void;
  // 🛰️ Hybrid Scope Props
  activeScope?: 'PERSONAL' | 'HOUSEHOLD' | 'BOTH';
  isPersonalMode?: boolean; // True if currentHouseholdId is null
  // 🛰️ UI injections
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>; 
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 🧱 WidgetContainer
 * Refactored for Hybrid Scoping: The shell handles visual partition cues
 * while maintaining Edit Mode and layout logic.
 */
export const WidgetContainer: React.FC<WidgetContainerProps> = ({
  id,
  title,
  children,
  isEditMode,
  onRemove,
  activeScope = 'BOTH',
  isPersonalMode = false,
  dragHandleProps,
  className,
  style
}) => {
  const { t } = useTranslation();

  const dragHandleClassName = dragHandleProps?.className || '';
  const otherDragHandleProps = { ...dragHandleProps };
  if (otherDragHandleProps.className) {
    delete otherDragHandleProps.className;
  }

  // 🎨 Partition Branding: Emerald for Personal, Indigo for Shared
  const isPersonalActive = isPersonalMode || activeScope === 'PERSONAL';

  return (
    <div
      style={style}
      className={`
        group relative flex flex-col bg-white dark:bg-slate-900
        rounded-2xl transition-all duration-500 overflow-hidden
        ${isEditMode
          ? 'shadow-[inset_0_0_0_2px_theme(colors.brand.DEFAULT)] cursor-grab active:cursor-grabbing'
          : 'border border-slate-100 dark:border-slate-800 shadow-sm'
        }
        ${className || ''}
      `}
    >
      {/* --- WIDGET HEADER --- */}
      <div className={`
        flex items-center justify-between px-4 h-10 transition-colors border-b border-slate-100 dark:border-slate-800/60
        ${isEditMode
          ? 'bg-brand/5 dark:bg-brand/5'
          : isPersonalActive
            ? 'bg-emerald-50/10 dark:bg-emerald-900/5'
            : 'bg-slate-50/30 dark:bg-slate-800/20'
        }
      `}>
        <div className="flex items-center gap-3">
          {/* 🛰️ DRAG HANDLE: Only active in Edit Mode */}
          {isEditMode && (
            <div
              {...otherDragHandleProps}
              className={`${dragHandleClassName} p-1.5 hover:bg-brand/10 dark:hover:bg-brand/10 rounded-lg cursor-move text-brand transition-colors`}
            >
              <GripVertical className="w-4 h-4" />
            </div>
          )}

          {/* 🛡️ SCOPE INDICATOR: Visual proof of privacy siloing */}
          {!isEditMode && (
             <div className="flex items-center">
                {isPersonalActive ? (
                  <Shield className="w-3 h-3 text-emerald-500/50" />
                ) : (
                  <Users className="w-3 h-3 text-brand/50" />
                )}
             </div>
          )}

          <h3 className={`text-2xs font-black uppercase tracking-[0.3em] transition-colors ${
            isPersonalActive ? 'text-emerald-600/60' : 'text-slate-400 dark:text-slate-500'
          }`}>
            {t('widget.title_placeholder', title)}
          </h3>
        </div>

        {/* --- SYSTEM ACTIONS --- */}
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <button 
              onClick={(e) => {
                e.stopPropagation(); 
                onRemove(id);
              }}
              className="p-2 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all active:scale-90"
              title={t('dash.remove_widget', 'Remove Widget')}
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <button className="p-2 text-slate-300 hover:text-brand dark:hover:text-brand transition-colors">
                <Settings2 className="w-4 h-4" />
              </button>
              <button className="p-2 text-slate-300 hover:text-brand dark:hover:text-brand transition-colors">
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* --- WIDGET CONTENT SLOT --- */}
      <div className="flex-1 overflow-hidden relative px-4 py-4">
        {/* 🛡️ EDIT OVERLAY */}
        {isEditMode && (
          <div className="absolute inset-0 z-50 bg-transparent cursor-grab active:cursor-grabbing" />
        )}
        
        <div className={`h-full w-full transition-all duration-300 ${
          isEditMode ? 'pointer-events-none blur-[2px] opacity-40' : ''
        }`}>
          {children}
        </div>
      </div>

      {/* 🚀 PARTITION FOOTER: Minimalist data-line */}
      {!isEditMode && (
        <div className={`h-[1px] w-full transition-colors ${
          isPersonalActive ? 'bg-emerald-500/20' : 'bg-transparent'
        }`} />
      )}
    </div>
  );
};